import styles from './SocialSharingModal.module.scss'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import travelObject from '@/entities/travel/interface'
import { Col, Modal, Row } from 'react-bootstrap'
import Icon from '@/components/elements/Icon'
import EmailIcon from '@mui/icons-material/Email'
import SvgIcon from '@mui/material/SvgIcon'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import LinkIcon from '@mui/icons-material/Link'
import CheckIcon from '@mui/icons-material/Check'
import { isMobile } from 'react-device-detect'
import { sendEventTravelSharingExecuted, sendEventTravelSharingOpened } from '@/lib/ga/gtag'
import { SocialSharingMethod } from '@/interfaces'

interface Props {
  travel: travelObject
  triggerElement: JSX.Element
}

const SocialSharingModal = ({ travel, triggerElement }: Props) => {
  const { t } = useTranslation('common')
  const [show, setShow] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)

  const handleClose = () => setShow(false)
  const handleShow = () => {
    setLinkCopied(false)
    setShow(true)
    sendEventTravelSharingOpened({ itemId: `${travel.id}` })
  }

  const XIcon = (props: any) => (
    <SvgIcon {...props} viewBox="0 0 1200 1227">
      <path
        d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z"
        fill="currentColor"
      />
    </SvgIcon>
  )

  const url = process.env.NEXT_PUBLIC_BASE_URL + '/tour/' + travel.seoSettings.urlSlug
  const buildUrl = (platform: string, encode = false) => {
    const utmCampaign = travel.pricing?.travels[0]?.catalogCode
      ? `${travel.pricing.travels[0].catalogCode}_${travel.pricing.travels[0].travelCode}`
      : 'no_travel_import'
    const utmUrl = `${url}?utm_source=${platform}&utm_medium=social&utm_campaign=${utmCampaign}&utm_content=website_share_button`
    if (!encode) {
      return utmUrl
    }
    return encodeURIComponent(utmUrl)
  }
  // const urlEncoded = encodeURIComponent(url)

  const sendEvent = (name: SocialSharingMethod) => {
    sendEventTravelSharingExecuted({ method: name, itemId: `${travel.id}` })
  }

  interface Platform {
    name: SocialSharingMethod
    label: string
    icon: JSX.Element
    url: string
    onClick?: (event: any) => void
  }
  const platforms: Platform[] = [
    {
      name: 'email',
      label: t('socialSharing.sendEmail'),
      icon: <EmailIcon className={styles.icon} />,
      url: `mailto:?subject=${travel.name} | ${process.env.NEXT_PUBLIC_SITE_NAME}&body=${buildUrl('email', true)}`,
    },
    {
      name: 'copy_link',
      label: t('socialSharing.copyLink'),
      icon: <>{!linkCopied ? <LinkIcon className={styles.icon} /> : <CheckIcon className={styles.icon} />}</>,
      onClick: (event: any) => {
        event.preventDefault()
        // event.stopPropagation()
        navigator.clipboard.writeText(buildUrl('sharing_link', false))
        setLinkCopied(true)
      },
      url: '#',
    },
    {
      name: 'whatsapp',
      label: 'WhatsApp',
      icon: <WhatsAppIcon className={styles.icon} />,
      url: isMobile ? 'whatsapp://send?text=' + buildUrl('whatsapp', true) : 'https://web.whatsapp.com/send?text=' + buildUrl('whatsapp', true),
    },
    {
      name: 'facebook',
      label: 'Facebook',
      icon: <Icon name={'facebook'} className={styles.icon} />,
      url: 'https://www.facebook.com/sharer/sharer.php?u=' + buildUrl('facebook', true),
    },
    {
      name: 'x',
      label: 'X',
      icon: <XIcon className={styles.icon} />,
      url: 'https://twitter.com/intent/tweet?url=' + buildUrl('twitter', true),
    },
  ]

  return (
    <>
      {React.cloneElement(triggerElement, { onClick: handleShow })}
      <Modal size={'lg'} show={show} onHide={handleClose} className={styles.modal} centered scrollable={false}>
        <button type="button" className={styles.close} aria-label="Close" onClick={handleClose}>
          <span aria-hidden="true">&times;</span>
        </button>
        <Modal.Body className={styles.body}>
          <div className={styles.heading}>
            <Modal.Title className={styles.title}>{t('socialSharing.title')}</Modal.Title>
            <p>{t('socialSharing.shareNow')}</p>
          </div>
          <Row className={'justify-content-center mt-3 mb-4'}>
            {platforms.map((platform, index: number) => {
              let onClick = (event: any) => {
                sendEvent(platform.name)
              }
              if (platform.onClick) {
                onClick = (event: any) => {
                  sendEvent(platform.name)
                  // @ts-ignore
                  platform.onClick(event)
                }
              }
              return (
                <Col
                  xs={'auto'}
                  as={'a'}
                  className={styles.sharingLink}
                  href={platform.url}
                  target={'_blank'}
                  rel="noreferrer"
                  key={index}
                  onClick={onClick}
                  data-tr-click="travel_page:sharing:execute"
                  data-tr-sharing_method={platform.name}
                >
                  <span className={styles.iconWrap}>{platform.icon}</span>
                  <span className={styles.label}>{platform.label}</span>
                </Col>
              )
            })}
          </Row>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default SocialSharingModal
